<template>
  <div class="wa-step">
    <h2 class="wa-step__heading">
      {{ t(headingKey) }}
    </h2>
    <p class="wa-step__lead">
      {{ t(leadKey) }}
    </p>
    <div class="wa-checklist-cards">
      <div
        v-for="(card, index) in cards"
        :key="index"
        class="wa-checklist-card"
      >
        <div
          class="wa-readonly-item__icon"
          :class="card.iconClass"
        >
          <i :class="card.icon" />
        </div>
        <div class="wa-checklist-card__body">
          <h3 class="wa-checklist-card__title">
            {{ t(card.titleKey) }}
          </h3>
          <p class="wa-checklist-card__text">
            {{ t(card.descriptionKey) }}
          </p>
          <a
            v-if="card.linkKey && card.linkUrl"
            :href="card.linkUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="wa-checklist-card__link"
          >
            {{ t(card.linkKey) }}
          </a>
        </div>
      </div>
    </div>
    <div
      v-if="variant === 'migration'"
      class="wa-step__alert"
      role="alert"
    >
      <i class="fa-solid fa-circle-info" />
      <span>{{ t('storeParameters.services.whatsappBusinessParameter.onboarding.shared.doNotCloseWindow') }}</span>
    </div>
    <p class="wa-step__legal">
      {{ t('storeParameters.services.whatsappBusinessParameter.onboarding.legal.textBeforeLinks') }}
      <a
        :href="legalUrls.businessTerms"
        target="_blank"
        rel="noopener noreferrer"
      >{{ t('storeParameters.services.whatsappBusinessParameter.onboarding.legal.businessTermsLink') }}</a>{{ t('storeParameters.services.whatsappBusinessParameter.onboarding.legal.textBetweenLinks') }}<a
        :href="legalUrls.businessSolutionTerms"
        target="_blank"
        rel="noopener noreferrer"
      >{{ t('storeParameters.services.whatsappBusinessParameter.onboarding.legal.solutionTermsLink') }}</a>{{ t('storeParameters.services.whatsappBusinessParameter.onboarding.legal.textAfterLinks') }}
    </p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import type { WhatsappChecklistCardConfig } from '@/types/whatsapp-onboarding';
import { WHATSAPP_LEGAL_URLS } from '@/types/whatsapp-onboarding';

export default defineComponent({
  name: 'WhatsappAccessChecklistStep',

  props: {
    variant: {
      type: String,
      default: 'standard',
      validator: (v: string) => ['standard', 'migration', 'other_bsp'].includes(v),
    },
    showDisplayName: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const { t } = useI18n();

    const headingKey = computed(() => {
      if (props.variant === 'migration') {
        return 'storeParameters.services.whatsappBusinessParameter.onboarding.paths.waApp.checklist.heading';
      }
      if (props.variant === 'other_bsp') {
        return 'storeParameters.services.whatsappBusinessParameter.onboarding.paths.otherBsp.accessChecklist.heading';
      }
      return 'storeParameters.services.whatsappBusinessParameter.onboarding.shared.accessChecklist.heading';
    });

    const leadKey = computed(() => {
      if (props.variant === 'migration') {
        return 'storeParameters.services.whatsappBusinessParameter.onboarding.paths.waApp.checklist.lead';
      }
      if (props.variant === 'other_bsp') {
        return 'storeParameters.services.whatsappBusinessParameter.onboarding.paths.otherBsp.accessChecklist.lead';
      }
      return 'storeParameters.services.whatsappBusinessParameter.onboarding.shared.accessChecklist.lead';
    });

    const cards = computed((): WhatsappChecklistCardConfig[] => {
      const base: WhatsappChecklistCardConfig[] = [
        {
          icon: 'fa-brands fa-facebook-f',
          iconClass: 'wa-readonly-item__icon--facebook',
          titleKey: 'storeParameters.services.whatsappBusinessParameter.onboarding.shared.accessChecklist.facebookTitle',
          descriptionKey: 'storeParameters.services.whatsappBusinessParameter.onboarding.shared.accessChecklist.facebookDescription',
        },
        {
          icon: 'fa-brands fa-meta',
          iconClass: 'wa-readonly-item__icon--meta',
          titleKey: 'storeParameters.services.whatsappBusinessParameter.onboarding.shared.accessChecklist.metaTitle',
          descriptionKey: 'storeParameters.services.whatsappBusinessParameter.onboarding.shared.accessChecklist.metaDescription',
        },
      ];

      if (props.showDisplayName) {
        base.push({
          icon: 'fa-solid fa-user',
          iconClass: 'wa-readonly-item__icon--user',
          titleKey: 'storeParameters.services.whatsappBusinessParameter.onboarding.shared.accessChecklist.displayNameTitle',
          descriptionKey: 'storeParameters.services.whatsappBusinessParameter.onboarding.shared.accessChecklist.displayNameDescription',
          linkKey: 'storeParameters.services.whatsappBusinessParameter.onboarding.shared.accessChecklist.displayNameLink',
          linkUrl: 'https://developers.facebook.com/docs/whatsapp/display-names',
        });
      }

      return base;
    });

    return { t, headingKey, leadKey, cards, legalUrls: WHATSAPP_LEGAL_URLS };
  },
});
</script>
